import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Minus, Plus, Instagram, AlertTriangle, ChevronDown } from 'lucide-react';
import theme from '../lib/theme';
import QuizSabrilCoach from '../components/QuizSabrilCoach';
import QuizMilestoneToast from '../components/QuizMilestoneToast';
import QuizDiagnosticBar from '../components/QuizDiagnosticBar';
import QuizIntrigueWidget from '../components/QuizIntrigueWidget';
import QuizQuestionContext from '../components/QuizQuestionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dynamic Sabril tips — react to the user's actual answers in real-time.
// Returns null if no dynamic tip applies (falls back to question.sabrilTip).
const getDynamicSabrilTip = (questionId, answers) => {
  const ca = answers.ca_mensuel;
  switch (questionId) {
    case 'matieres_premieres_mensuel': {
      const fc = answers.food_cost;
      if (!ca || !fc) return null;
      if (fc <= 28) return { message: "Excellent food cost. T'es dans le top 10%. On va surtout chercher du levier ailleurs.", tone: 'green' };
      if (fc <= 32) return { message: "Food cost sain. On peut grappiller 1-2 points mais c'est pas l'urgence.", tone: 'green' };
      if (fc <= 38) return { message: "À surveiller. T'es au-dessus de la moyenne saine — on peut récupérer plusieurs milliers d'€/mois ici.", tone: 'warning' };
      return { message: "Aïe, là on a un vrai sujet. À ce niveau de food cost, on parle de 4 000 à 8 000€/mois qui te passent sous le nez. On va creuser ça dans votre audit.", tone: 'warning' };
    }
    case 'masse_salariale_mensuelle': {
      const ms = answers.masse_salariale;
      if (!ca || !ms) return null;
      if (ms <= 28) return { message: "Masse salariale sous contrôle. Bien joué.", tone: 'green' };
      if (ms <= 34) return { message: "Niveau sain. À condition que votre équipe tourne sans vous — sinon on a un sujet d'organisation.", tone: 'default' };
      if (ms <= 40) return { message: "À surveiller. Soit ton équipe est surdimensionnée, soit ta productivité est trop basse. On creuse ça.", tone: 'warning' };
      return { message: "À ce niveau, t'as soit trop de monde, soit pas assez de chiffre. C'est le 2e levier qu'on regarde dans votre audit.", tone: 'warning' };
    }
    case 'ticket_moyen': {
      const ticket = answers.ticket_moyen;
      if (!ticket) return null;
      if (ticket < 12) return { message: "Ticket très bas. Soit c'est le concept (fast-food), soit il y a un sujet de pricing à revoir.", tone: 'warning' };
      if (ticket > 35) return { message: "Bon ticket. L'enjeu c'est la fréquence et la fidélité, pas le panier.", tone: 'green' };
      return null;
    }
    case 'autonomie': {
      const a = answers.autonomie;
      if (a === 'pas_1_jour') return { message: "Là c'est urgent. Vous vous êtes créé un travail, pas un business. Sans vous, tout s'arrête — c'est votre priorité #1 à régler.", tone: 'warning' };
      if (a === 'semaine_plus') return { message: "T'as bossé l'organisation. Bravo. On va pouvoir se concentrer sur la croissance.", tone: 'green' };
      return null;
    }
    case 'organisation_semaine': {
      const o = answers.organisation_semaine;
      if (o === '100_op') return { message: "Vous foncez droit dans le mur — burn-out garanti. Il faut sortir la tête de l'eau pour piloter.", tone: 'warning' };
      if (o === 'surtout_pilotage') return { message: "T'es au bon endroit. Maintenant on optimise la machine.", tone: 'green' };
      return null;
    }
    case 'concept_clair': {
      const c = answers.concept_clair;
      if (c && c <= 2) return { message: "Concept flou = clients qui hésitent = ticket moyen plus bas. C'est un levier énorme à reposer.", tone: 'warning' };
      if (c && c >= 4.5) return { message: "Concept clair, c'est ta force. À exploiter à fond en marketing.", tone: 'green' };
      return null;
    }
    default:
      return null;
  }
};

// ============================================================
// OUVERTURE FLOW — Questions dédiées aux projets d'ouverture
// (déclenché si project_status === 'en_ouverture')
// ============================================================
const OUVERTURE_SCREENS = [
  {
    id: 'restaurant',
    title: 'TON PROJET',
    icon: '🚀',
    sabrilTip: "Salut, c'est Sabril. Vous préparez une ouverture — c'est l'étape la plus délicate. On va cadrer votre projet ensemble étape par étape.",
    questions: [
      { id: 'project_status', type: 'choice', label: 'Où en êtes-vous avec votre projet ?', options: [
        { value: 'deja_ouvert', label: '🍽️ Resto déjà ouvert · je veux passer au niveau supérieur' },
        { value: 'en_ouverture', label: '🚀 En ouverture · je prépare le lancement' },
        { value: 'multi_resto', label: '🏢 Plusieurs restos · je veux structurer mon groupe' },
        { value: 'reflexion', label: '🤔 Je réfléchis · juste un check-up santé' }
      ], sabrilTip: "Cette réponse va m'aider à réaliser l'audit le plus précis pour vous." },
      { id: 'resto_name', type: 'text', label: 'Nom (provisoire) de ton resto', placeholder: 'Ex : Le Petit Bistrot' },
      { id: 'resto_type', type: 'grid', label: 'Type de cuisine prévu', options: [
        { value: 'fastfood', label: '🍔 Fast-food' }, { value: 'streetfood', label: '🌮 Street food' },
        { value: 'traditionnel', label: '🍳 Traditionnel' }, { value: 'gastro', label: '🍽️ Gastronomique' },
        { value: 'monde', label: '🌍 Cuisine du monde' }, { value: 'healthy', label: '🥗 Healthy-Végé' },
        { value: 'pizzeria', label: '🍕 Pizzeria' }, { value: 'autre', label: '✨ Pas encore décidé' }
      ]}
    ]
  },
  {
    id: 'ouverture_projet',
    title: 'TON PROJET D\'OUVERTURE',
    icon: '🏗️',
    sabrilTip: "Plus vous êtes précis, plus mon diagnostic sera tranchant. Les ouvertures qui réussissent ont une chose en commun : un cadre clair AVANT le coup de feu.",
    sabrilTone: 'warning',
    questions: [
      { id: 'ouverture_echeance', type: 'choice', label: 'Quand prévoyez-vous d\'ouvrir ?', options: [
        { value: 'moins_3m', label: '⚡ Moins de 3 mois — c\'est imminent' },
        { value: '3_6m', label: '🎯 3 à 6 mois' },
        { value: '6_12m', label: '📅 6 à 12 mois' },
        { value: 'plus_12m', label: '🌱 Plus de 12 mois / pas encore décidé' }
      ], sabrilTip: "Une ouverture précipitée = 80% des problèmes futurs. Mieux vaut décaler de 2 mois que rater le lancement." },
      { id: 'ouverture_local', type: 'choice', label: 'Et le local ?', options: [
        { value: 'signe', label: '✅ Signé — j\'ai les clés' },
        { value: 'en_nego', label: '🤝 En négociation' },
        { value: 'recherche', label: '🔍 En recherche active' },
        { value: 'pas_encore', label: '🤔 Pas encore commencé' }
      ], sabrilTip: "Le local décide de 50% du succès. Surface, flux, voisinage : on regarde tout avant de signer." },
      { id: 'ouverture_budget', type: 'currency_input', label: 'Quel est votre budget global d\'ouverture ?', description: 'Travaux, matériel, marketing, fonds de roulement. Une estimation honnête.', placeholder: '150000', min: 20000, max: 800000, step: 5000, unit: '€', defaultValue: 120000, sabrilTip: "Un resto qui ouvre sans fonds de roulement (3 mois de charges en banque) meurt 80% du temps en année 1." },
      { id: 'ouverture_ville', type: 'text', label: 'Ville / zone d\'implantation', placeholder: 'Ex : Paris 11e, Lyon centre, Marrakech Guéliz' }
    ]
  },
  {
    id: 'ouverture_chantiers',
    title: 'CE QUE TU VEUX CONSTRUIRE',
    icon: '🛠️',
    sabrilTip: "Sélectionnez TOUT ce sur quoi vous voulez qu'on travaille ensemble. Plus vous cochez, plus on a de matière pour structurer votre ouverture.",
    questions: [
      { id: 'ouverture_chantiers', type: 'multi', label: 'Sur quoi vous voulez qu\'on travaille ?', description: 'Sélection multiple — coche tout ce qui te parle', options: [
        { value: 'marque', label: '🎨 Construire la marque', sub: 'Nom, logo, identité, ton' },
        { value: 'concept', label: '💡 Définir le concept', sub: 'Carte, expérience, positionnement' },
        { value: 'ops', label: '⚙️ Structurer l\'opérationnel', sub: 'Process, fiches techniques, normes' },
        { value: 'equipe', label: '👨‍🍳 Recruter et former l\'équipe', sub: 'Profils, scripts, formation' },
        { value: 'marketing', label: '📱 Stratégie marketing & lancement', sub: 'Pré-ouverture, réseaux, presse' },
        { value: 'local', label: '🏢 Trouver / négocier le local', sub: 'Surface, emplacement, bail' },
        { value: 'business_plan', label: '📊 Business plan & financement', sub: 'Prévi, banque, BPI' },
        { value: 'travaux', label: '🔨 Aménagement & travaux', sub: 'Architecte, agencement, déco' }
      ]}
    ]
  },
  {
    id: 'ouverture_experience',
    title: 'TON EXPÉRIENCE',
    icon: '🎯',
    sabrilTip: "Pas de mauvaise réponse ici. C'est juste pour calibrer la profondeur de votre audit.",
    questions: [
      { id: 'ouverture_experience', type: 'choice', label: 'Avez-vous déjà ouvert un restaurant ?', options: [
        { value: 'jamais', label: '🌱 Jamais — c\'est ma première fois' },
        { value: 'une_fois', label: '🎯 Oui, une fois' },
        { value: 'plusieurs', label: '🏆 Plusieurs fois — je connais le terrain' }
      ]},
      { id: 'ouverture_peurs', type: 'multi', label: 'Ta plus grosse peur aujourd\'hui ?', description: 'Sélectionne max 2 réponses', max: 2, options: [
        { value: 'planter', label: '😰 Se planter complètement' },
        { value: 'budget', label: '💸 Dépasser le budget' },
        { value: 'equipe', label: '👥 Mal recruter / mal manager' },
        { value: 'clients', label: '🚪 Ne pas avoir de clients' },
        { value: 'concept', label: '❓ Concept pas assez clair' },
        { value: 'solitude', label: '😶 Être seul à porter le projet' }
      ]}
    ]
  },
  {
    id: 'contact',
    title: 'TON DIAGNOSTIC EST PRÊT',
    icon: '✅',
    sabrilTip: "Plus que 30 secondes. Je vous envoie votre diagnostic d'ouverture direct sur WhatsApp + email. Aucun spam, c'est moi qui vous écris.",
    sabrilTone: 'green',
    showReassurance: true,
    reassuranceBlock: {
      title: 'Ton diagnostic d\'ouverture est prêt.',
      points: [
        '🚀 Accède à ton plan d\'action immédiatement après cette étape.',
        '🛡️ Nous avons identifié les risques #1 de votre projet et comment les neutraliser.'
      ]
    },
    questions: [
      { id: 'first_name', type: 'text_input', label: 'Votre prénom', placeholder: 'Prénom', required: true },
      { id: 'phone', type: 'phone_input', label: 'Ton WhatsApp', placeholder: '06 12 34 56 78', required: true, description: 'On t\'envoie votre diagnostic par WhatsApp 📱', isWhatsApp: true },
      { id: 'email', type: 'text_input', label: 'Ton email', placeholder: 'ton@email.com', required: true, inputType: 'email', description: 'Pour recevoir ton plan d\'action personnalisé par mail 📩' }
    ]
  }
];

// Quiz configuration
const QUIZ_SCREENS = [
  {
    id: 'restaurant',
    title: 'TON RESTAURANT',
    icon: '🍽️',
    sabrilTip: "Salut, c'est Sabril. Avant de plonger dans vos chiffres, dites-moi qui vous êtes. Aucune mauvaise réponse ici, on va y aller étape par étape.",
    subdivideQuestions: true,
    questions: [
      {
        id: 'project_status', type: 'choice',
        label: 'Où en êtes-vous avec votre projet ?',
        options: [
          { value: 'deja_ouvert', label: '🍽️ Resto déjà ouvert · je veux passer au niveau supérieur' },
          { value: 'en_ouverture', label: '🚀 En ouverture · je prépare le lancement' },
          { value: 'multi_resto', label: '🏢 Plusieurs restos · je veux structurer mon groupe' },
          { value: 'reflexion', label: '🤔 Je réfléchis · juste un check-up santé' }
        ],
        why: "Ton point de départ change tout. Un resto en projet, un resto qui tourne et un groupe à structurer n'ont pas les mêmes priorités.",
        benefit: "Je calibre tout l'audit sur ta situation réelle · pas un diagnostic générique mais une lecture précise de ton moment.",
        sabrilTip: "Cette réponse va m'aider à réaliser l'audit le plus précis pour vous.",
      },
      {
        id: 'resto_name', type: 'text',
        label: 'Nom de ton resto',
        placeholder: 'Ex : Le Petit Bistrot',
        why: "Connaître votre prénom me permet de personnaliser votre audit. Vous verrez votre vrai nom de resto dans le diagnostic final.",
        benefit: "Ton audit final aura ton nom · pas un PDF générique mais un document qui parle de TON resto.",
      },
      {
        id: 'resto_type', type: 'grid',
        label: 'Type de cuisine',
        options: [
          { value: 'fastfood', label: '🍔 Fast-food' }, { value: 'streetfood', label: '🌮 Street food' },
          { value: 'traditionnel', label: '🍳 Traditionnel' }, { value: 'gastro', label: '🍽️ Gastronomique' },
          { value: 'monde', label: '🌍 Cuisine du monde' }, { value: 'healthy', label: '🥗 Healthy-Végé' },
          { value: 'pizzeria', label: '🍕 Pizzeria' }, { value: 'autre', label: '✨ Autre' }
        ],
        why: "Chaque type de cuisine a ses propres ratios sains (food cost, marge, ticket moyen). Un gastro et un fast-food ne se pilotent pas pareil.",
        benefit: "Je compare ton resto au benchmark de TA catégorie · pas une moyenne nationale floue.",
      }
    ]
  },
  {
    id: 'finances',
    title: 'FINANCES & RENTABILITÉ',
    icon: '💰',
    sabrilTip: "OK, là on entre dans le dur. Soyez précis — vos chiffres réels, pas ceux que vous aimeriez avoir. C'est là où votre audit gagne ou perd 90% de sa précision.",
    sabrilTone: 'warning',
    subdivideQuestions: true,  // ← affiche UNE question par écran avec contexte Sabril enrichi
    questions: [
      {
        id: 'ca_mensuel', type: 'currency_input',
        label: 'Quel est votre CA mensuel ?',
        description: 'Entre ton chiffre d\'affaires mensuel précis',
        placeholder: '75000', min: 5000, max: 500000, step: 1000, unit: '€', defaultValue: 50000,
        why: "Le CA, c'est l'ancrage de tout l'audit. Sans ce chiffre, je ne peux pas calculer tes vrais ratios ni te dire où sont tes leviers.",
        benefit: "Je positionne votre resto par rapport au benchmark du secteur (food cost / masse sal / EBE) et je calcule combien vous pourriez gagner en plus chaque mois.",
        confidential: true,
        sabrilTip: "Soyez honnête — chiffres réels, pas ceux que vous aimeriez avoir.", sabrilTone: 'default',
      },
      {
        id: 'matieres_premieres_mensuel', type: 'currency_input',
        label: 'Combien dépensez-vous en matières premières par mois ?',
        description: 'Tes achats nourriture, boissons, consommables. On calcule votre food cost automatiquement.',
        placeholder: '15000', min: 500, max: 200000, step: 500, unit: '€', defaultValue: 15000,
        autoComputePercent: { of: 'ca_mensuel', storeAs: 'food_cost' },
        why: "Le food cost, c'est LE chiffre #1 d'un resto. La majorité des restos qui ferment ne le connaissent même pas.",
        benefit: "Je calcule votre food cost en % et je te dis exactement combien vous perdez chaque mois (entre 0 et 8 000€ chez certains).",
        sabrilTip: "Achats matières premières uniquement — pas les emballages.", sabrilTone: 'warning',
      },
      {
        id: 'masse_salariale_mensuelle', type: 'currency_input',
        label: 'Combien coûte ta masse salariale par mois ?',
        description: 'Salaires + charges de tous tes employés. On calcule ton % automatiquement.',
        placeholder: '18000', min: 500, max: 200000, step: 500, unit: '€', defaultValue: 15000,
        autoComputePercent: { of: 'ca_mensuel', storeAs: 'masse_salariale' },
        why: "C'est le 2e poste de coût après les matières. Beaucoup de restos s'épuisent à payer leur équipe sans s'en rendre compte.",
        benefit: "Je détecte si t'es au-dessus du seuil critique (35-40%) et je te donne les leviers concrets pour redescendre.",
        sabrilTip: "Comptez TOUT : salaires bruts + charges patronales + votre propre salaire.", sabrilTone: 'warning',
      },
      {
        id: 'loyer_mensuel', type: 'currency_input',
        label: 'Quel est ton loyer mensuel ?',
        description: 'Loyer charges comprises',
        placeholder: '3500', min: 500, max: 50000, step: 100, unit: '€', defaultValue: 3000,
        why: "Le loyer, c'est un coût fixe qui te bouffe ton EBE silencieusement. Au-dessus de 12% du CA, ça étouffe.",
        benefit: "Je calcule ton ratio loyer/CA et je te dis si t'as une marge de manœuvre pour renégocier (oui, c'est possible).",
      },
      {
        id: 'ticket_moyen', type: 'slider_euro',
        label: 'Ton ticket moyen par personne ?',
        min: 5, max: 60, step: 1, unit: '€', defaultValue: 18,
        why: "Le ticket moyen révèle votre positionnement réel · pas celui que vous crois avoir.",
        benefit: "Je vous dis si vous pouvez augmenter vos prix sans perdre de clients (presque toujours possible) et de combien.",
      },
    ]
  },
  {
    id: 'problemes',
    title: 'TES DÉFIS',
    icon: '🎯',
    sabrilTip: "Là vous allez me dire ce qui vous bouffe la tête la nuit. C'est EXACTEMENT ce qu'on regarde en priorité dans votre audit — pas le marketing, pas l'expansion.",
    subdivideQuestions: true,
    questions: [
      {
        id: 'plus_gros_probleme', type: 'multi',
        label: 'Quels sont tes plus gros problèmes aujourd\'hui ?',
        description: 'Vous pouvez en sélectionner plusieurs · choisissez tous ceux qui vous parlent',
        options: [
          { value: 'marge_faible', label: '💸 Marge trop faible' }, { value: 'marketing', label: '📱 Marketing inexistant' },
          { value: 'manque_clients', label: '👥 Manque de clients' }, { value: 'equipe', label: '👨‍🍳 Équipe difficile à gérer' },
          { value: 'organisation', label: '📋 Organisation interne' }
        ],
        why: "C'est la question la plus importante du quiz. Les problèmes qui te bouffent le plus la tête sont aussi ceux qui te coûtent le plus en € chaque mois.",
        benefit: "Je centre votre audit sur VOS vrais problèmes en priorité · pas sur ce qui m'intéresse, sur ce qui vous débloque.",
        sabrilTip: "Sélectionnez tous ceux qui vous parlent · pas juste un. Vous savez lesquels.",
      },
      {
        id: 'objectif_principal', type: 'choice',
        label: 'Quel est ton objectif principal ?',
        options: [
          { value: 'structurer', label: '⚙️ Structurer l\'organisation' }, { value: 'augmenter_ca', label: '📈 Augmenter le CA' },
          { value: 'developper_concept', label: '🚀 Développer un concept' }, { value: 'ameliorer_rentabilite', label: '💰 Améliorer la rentabilité' }
        ],
        why: "Beaucoup de patrons résolvent les mauvais problèmes. Connaître ton objectif final me permet d'orienter le plan d'action dans la bonne direction.",
        benefit: "Ton audit sera aligné sur TON cap · pas un plan générique mais une route vers ton objectif réel.",
      }
    ]
  },
  {
    id: 'marketing',
    title: 'MARKETING & VISIBILITÉ',
    icon: '📱',
    sabrilTip: "90% des restos perdent ici sans le savoir. Répondez franchement, on regarde après ce qui peut vraiment bouger votre CA.",
    subdivideQuestions: true,
    questions: [
      {
        id: 'instagram_freq', type: 'number_stepper',
        label: 'Combien de fois par semaine postez-vous sur Instagram ?',
        min: 0, max: 14, step: 1, defaultValue: 2,
        why: "Instagram c'est l'enseigne digitale n°1 d'un resto en 2026. Sous 3 posts/semaine, vous perdez en visibilité chaque jour.",
        benefit: "Je te donne le bon rythme à tenir pour ton type de resto + les formats qui marchent vraiment dans TA zone.",
      },
      {
        id: 'contenu_type', type: 'choice',
        label: 'Ton contenu c\'est quoi principalement ?',
        options: [
          { value: 'photos', label: '📷 Photos de plats uniquement' }, { value: 'rien', label: '😴 Je poste pas vraiment' },
          { value: 'storytelling', label: '🎬 Storytelling + coulisses + moi' }
        ],
        why: "Les photos de plats ne suffisent plus. Les restos qui explosent montrent le patron, l'équipe, l'envers du décor.",
        benefit: "Je te dis quel type de contenu shooter pour multiplier tes vues x3 sans changer de produit.",
      },
      {
        id: 'note_google', type: 'rating',
        label: 'Ta note Google ?',
        description: 'De 1 à 5 étoiles',
        min: 1, max: 5, step: 0.5, defaultValue: 4,
        why: "Sous 4.3 sur Google, t'es invisible dans les recherches « resto + ta ville ». C'est aussi simple que ça.",
        benefit: "Je te dis si ta note te coûte des clients chaque jour et comment la faire monter en 30 jours.",
      },
      {
        id: 'avis_google_count', type: 'choice',
        label: 'Combien d\'avis Google avez-vous ?',
        options: [
          { value: '50_200', label: '📊 50 - 200' }, { value: 'plus_500', label: '🌟 Plus de 500' },
          { value: 'moins_50', label: '📝 Moins de 50' }, { value: '200_500', label: '⭐ 200 - 500' }
        ],
        why: "Le nombre d'avis crée la confiance avant même que le client n'entre. Sous 100 avis, t'es perçu comme « nouveau » ou « pas terrible ».",
        benefit: "Je te dis exactement combien d'avis viser sur 90 jours et comment les obtenir naturellement (sans tricher).",
      }
    ]
  },
  {
    id: 'digital',
    title: 'DIGITAL & LIVRAISON',
    icon: '🛵',
    sabrilTip: "Le digital, c'est plus une option en 2026. On va voir où vous en êtes — pas pour vous juger, pour identifier les leviers à activer.",
    subdivideQuestions: true,
    questions: [
      {
        id: 'plateformes_livraison', type: 'choice',
        label: 'Vous êtes sur les plateformes de livraison ?',
        options: [
          { value: 'non', label: '❌ Non' }, { value: 'oui_optimise', label: '✅ Oui et c\'est optimisé' },
          { value: 'oui_pas_optimise', label: '⚠️ Oui mais pas optimisé' }
        ],
        why: "Uber Eats / Deliveroo représentent 15-30% du CA des restos qui s'y prennent bien. Mal optimisé, c'est du CA brûlé en commissions.",
        benefit: "Je vous dis si c'est rentable POUR vous (ou pas) et comment optimiser votre carte digitale pour booster votre marge.",
      },
      {
        id: 'click_collect', type: 'toggle',
        label: 'Faites-vous du click & collect ?',
        options: [{ value: true, label: 'Oui' }, { value: false, label: 'Non' }],
        why: "Le click & collect, c'est de la marge pleine (zéro commission). 80% des restos qui pourraient en faire n'en font pas.",
        benefit: "Je vous dis combien vous pourriez ajouter par mois en activant le click & collect sur votre page Google + Instagram.",
      },
      {
        id: 'menu_en_ligne', type: 'choice',
        label: 'T\'as un menu en ligne à jour ?',
        options: [
          { value: 'plateformes', label: '📱 Juste sur les plateformes' }, { value: 'oui_complet', label: '✅ Oui sur Google + site web' },
          { value: 'non', label: '❌ Non pas du tout' }
        ],
        why: "80% des clients regardent ton menu avant de venir. Un menu obsolète ou caché = clients perdus avant même qu'ils n'arrivent.",
        benefit: "Je te dis exactement où ton menu doit apparaître + comment le rendre cliquable pour capter ces 80% qui hésitent.",
      }
    ]
  },
  {
    id: 'operations',
    title: 'OPÉRATIONS & ÉQUIPE',
    icon: '⚙️',
    sabrilTip: "Si vous êtes absent et que ça tourne pas, vous n'avez pas un resto — vous avez juste un boulot que vous vous êtes créé. On va corriger ça.",
    sabrilTone: 'warning',
    subdivideQuestions: true,
    questions: [
      {
        id: 'autonomie', type: 'choice',
        label: 'Si t\'es absent, ton resto tourne combien de temps ?',
        options: [
          { value: '2_3_jours', label: '⏰ 2-3 jours max' }, { value: 'pas_1_jour', label: '🔥 Même pas une journée' },
          { value: 'semaine_plus', label: '🏖️ Plus d\'une semaine' }
        ],
        why: "La vraie question : ton resto a-t-il besoin de TOI pour exister ? Tant que la réponse est oui, t'as pas un actif · t'as un boulot.",
        benefit: "Je te dis exactement où sont tes points de dépendance et je te donne le plan pour rendre ton resto autonome en 90 jours.",
        sabrilTip: "Répondez franchement · personne ne va vous juger ici.",
      },
      {
        id: 'organisation_semaine', type: 'choice',
        label: 'Comment organisez-vous votre semaine de dirigeant ?',
        options: [
          { value: '100_op', label: '🔥 100% opérationnel — tout le temps en cuisine/salle' },
          { value: '70_op', label: '⚖️ 70% opérationnel · 30% pilotage' },
          { value: '50_50', label: '🎯 50/50 — opérationnel + chiffres/équipe/marketing' },
          { value: 'surtout_pilotage', label: '👔 Surtout pilotage — l\'équipe tourne sans moi' }
        ],
        why: "Un patron qui passe 100% de son temps en service ne pilote pas · il s'épuise. Le vrai levier c'est le temps que vous passes SUR le resto, pas DANS.",
        benefit: "Je calcule ton ratio « opérationnel vs pilotage » et je te dis comment libérer 10-15h/semaine pour bosser sur la croissance.",
      },
      {
        id: 'process', type: 'choice',
        label: 'Tes process équipe ?',
        options: [
          { value: 'oral', label: '🗣️ Quelques consignes à l\'oral' }, { value: 'ecrits', label: '📝 Tout est écrit et documenté' },
          { value: 'tete', label: '🧠 Tout est dans ma tête' }
        ],
        why: "Les process dans la tête = formation longue, erreurs récurrentes, équipe qui te sollicite en permanence. Les process écrits = équipe autonome.",
        benefit: "Je te dis exactement quels 3 process écrire en premier pour gagner des heures par jour (et te libérer mentalement).",
      },
      {
        id: 'heures_semaine', type: 'slider_number',
        label: 'Combien d\'heures par semaine travaillez-vous ?',
        min: 30, max: 90, step: 1, unit: 'h', defaultValue: 55,
        why: "Au-delà de 55h/semaine, vous rentrez en zone burn-out. Et plus vous travaillez, moins votre resto vous rapporte par heure.",
        benefit: "Je te calcule ton taux horaire réel (souvent moins que le SMIC, ça choque) et je te montre comment redescendre sous 45h sans perdre de CA.",
      }
    ]
  },
  {
    id: 'concept',
    title: 'CONCEPT & CARTE',
    icon: '🍽️',
    sabrilTip: "Un concept flou = des clients qui ne reviennent pas. On creuse pour voir si votre positionnement est aussi clair pour vos clients que pour vous.",
    subdivideQuestions: true,
    questions: [
      {
        id: 'concept_clair', type: 'rating',
        label: 'Ton concept est clair pour vos clients ?',
        description: '1 = Flou même pour moi · 5 = Tout le monde le répète',
        min: 1, max: 5,
        why: "Si vous-même hésitez à décrire votre concept en une phrase, vos clients hésitent aussi. Et un client qui hésite, c'est un client qui ne recommande pas.",
        benefit: "Je détecte le niveau de clarté de votre positionnement et je te donne une phrase d'accroche claire à utiliser partout (instagram, carte, employés).",
        sabrilTip: "Si vous hésitez, c'est que c'est pas clair. Vos clients hésitent aussi.",
      },
      {
        id: 'plat_signature', type: 'choice',
        label: 'T\'as un plat signature dont les gens parlent ?',
        options: [
          { value: 'pas_vraiment', label: '🤷 Pas vraiment' }, { value: 'oui', label: '⭐ Oui, connu et réclamé' }, { value: 'non', label: '❌ Non' }
        ],
        why: "Le plat signature, c'est ce qui rend ton resto IRREMPLAÇABLE. Sans lui, t'es interchangeable avec le voisin.",
        benefit: "Je te dis comment créer (ou booster) un plat signature qui devient votre marque · c'est l'arme #1 pour faire venir des clients récurrents.",
      },
      {
        id: 'nb_plats', type: 'number_stepper',
        label: 'Combien de plats avez-vous à la carte ?',
        min: 3, max: 60, step: 1, defaultValue: 20,
        why: "Au-delà de 25 plats, ton stock devient ingérable, votre food cost explose et la qualité baisse mécaniquement.",
        benefit: "Je te dis combien de plats supprimer pour booster votre marge sans perdre de clients (souvent +3 points de food cost récupérés).",
      }
    ]
  },
  {
    id: 'contact',
    title: 'TON DIAGNOSTIC EST PRÊT',
    icon: '✅',
    sabrilTip: "Plus que 30 secondes. Je vous envoie votre diagnostic personnalisé direct sur WhatsApp + email. Aucun spam, c'est moi qui vous écris.",
    sabrilTone: 'green',
    showReassurance: true,
    reassuranceBlock: {
      title: 'Ton diagnostic est prêt.',
      points: [
        '👉 Accède à ton analyse complète immédiatement après cette étape.',
        '💰 Nous avons identifié plusieurs axes d\'optimisation dans ton restaurant.'
      ]
    },
    questions: [
      { id: 'first_name', type: 'text_input', label: 'Votre prénom', placeholder: 'Prénom', required: true },
      { id: 'phone', type: 'phone_input', label: 'Ton WhatsApp', placeholder: '06 12 34 56 78', required: true, description: 'On t\'envoie votre diagnostic par WhatsApp 📱', isWhatsApp: true },
      { id: 'email', type: 'text_input', label: 'Ton email', placeholder: 'ton@email.com', required: true, inputType: 'email', description: 'Pour recevoir ton plan d\'action personnalisé par mail 📩' }
    ]
  }
];

// Multi-select component (checkbox style, optional max selections)
const MultiSelect = ({ question, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const max = question.max || question.options.length;
  const atMax = selected.length >= max;

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      if (atMax) return; // ignore add when limit hit
      onChange([...selected, val]);
    }
  };

  return (
    <div className="space-y-3">
      {question.description && (
        <p className="text-sm" style={{ color: theme.textMuted }}>
          {question.description}
          {question.max ? ` · ${selected.length}/${question.max}` : ''}
        </p>
      )}
      <div className="space-y-2.5">
        {question.options.map((option) => {
          const isSelected = selected.includes(option.value);
          const isDisabled = atMax && !isSelected;
          return (
            <motion.button
              key={option.value}
              whileTap={{ scale: isDisabled ? 1 : 0.98 }}
              onClick={() => toggle(option.value)}
              disabled={isDisabled}
              data-testid={`multi-option-${option.value}`}
              className="w-full min-h-[64px] rounded-2xl px-4 py-3 text-left transition-all duration-200 flex items-center gap-3"
              style={{
                backgroundColor: isSelected ? theme.orangeDim : theme.card,
                border: isSelected ? `2px solid ${theme.orange}` : `1px solid ${theme.border}`,
                opacity: isDisabled ? 0.45 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                style={{
                  backgroundColor: isSelected ? theme.orange : 'transparent',
                  border: isSelected ? 'none' : `2px solid ${theme.border}`,
                }}
              >
                {isSelected && <Check className="w-4 h-4 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px]" style={{ color: isSelected ? theme.text : theme.textMuted }}>
                  {option.label}
                </div>
                {option.sub && (
                  <div className="text-[11px] mt-0.5" style={{ color: theme.textLight }}>
                    {option.sub}
                  </div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

// Standard choice button
const OptionButton = ({ option, selected, onClick }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="w-full min-h-[60px] rounded-2xl px-5 py-4 text-left transition-all duration-200 flex items-center justify-between gap-3"
    style={{
      backgroundColor: selected ? theme.orangeDim : theme.card,
      border: selected ? `2px solid ${theme.orange}` : `1px solid ${theme.border}`,
    }}
  >
    <span className="font-semibold text-[15px]" style={{ color: selected ? theme.text : theme.textMuted }}>
      {option.label}
    </span>
    {selected && (
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.orange }}>
        <Check className="w-4 h-4 text-white" />
      </motion.div>
    )}
  </motion.button>
);

// Grid button with AI suggestion
const GridButton = ({ option, selected, onClick, suggested }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="relative min-h-[60px] rounded-2xl px-4 py-3 text-center transition-all duration-200 flex flex-col items-center justify-center gap-1"
    style={{
      backgroundColor: selected ? theme.orangeDim : suggested ? '#f3e8ff' : theme.card,
      border: selected ? `2px solid ${theme.orange}` : suggested ? '2px solid #a855f7' : `1px solid ${theme.border}`,
    }}
  >
    {suggested && !selected && (
      <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500 text-white">
        IA ✨
      </span>
    )}
    <span className="font-semibold text-[13px]" style={{ color: selected ? theme.text : suggested ? '#7c3aed' : theme.textMuted }}>
      {option.label}
    </span>
  </motion.button>
);

// Cuisine type grid with AI detection
const CuisineTypeGrid = ({ question, value, onChange, restoName, instagram }) => {
  const [detectedType, setDetectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Auto-detect cuisine type
  useEffect(() => {
    const detectCuisine = async () => {
      if ((!restoName || restoName.length < 3) && !instagram) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          restaurant_name: restoName || '',
          instagram_bio: instagram || ''
        });
        const res = await fetch(`${API_URL}/api/detect/cuisine-type?${params}`);
        const data = await res.json();
        if (data.detected && data.confidence > 0.3) {
          // Map backend type to frontend value
          const typeMap = {
            'fast_food': 'fastfood',
            'street_food': 'streetfood',
            'traditionnel': 'traditionnel',
            'gastronomique': 'gastro',
            'pizzeria': 'pizzeria',
            'healthy': 'healthy',
            'asiatique': 'monde',
            'coffee_shop': 'autre'
          };
          setDetectedType(typeMap[data.detected] || data.detected);
        }
      } catch (err) {
        console.error('Cuisine detection failed:', err);
      }
      setLoading(false);
    };
    
    const timer = setTimeout(detectCuisine, 300);
    return () => clearTimeout(timer);
  }, [restoName, instagram]);
  
  return (
    <div className="space-y-3">
      {/* AI Detection indicator */}
      {loading && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="flex items-center justify-center gap-2 py-2 rounded-xl"
          style={{ backgroundColor: '#f3e8ff' }}
        >
          <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs font-medium text-purple-700">🔍 Détection du type de cuisine...</span>
        </motion.div>
      )}
      
      {/* Suggestion banner */}
      {detectedType && !value && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-xl bg-purple-50 border-2 border-purple-200"
        >
          <p className="text-sm text-purple-700 text-center">
            🎯 On pense que ton restaurant est : <strong>{question.options.find(o => o.value === detectedType)?.label || detectedType}</strong>
          </p>
          <p className="text-xs text-purple-500 text-center mt-1">Clique pour confirmer ou choisis un autre type</p>
        </motion.div>
      )}
      
      {/* Grid */}
      <div className="grid grid-cols-2 gap-3">
        {question.options.map((option) => (
          <GridButton 
            key={option.value} 
            option={option} 
            selected={value === option.value} 
            suggested={detectedType === option.value && !value}
            onClick={() => onChange(option.value)} 
          />
        ))}
      </div>
    </div>
  );
};

// Slider component
const SliderInput = ({ question, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value ?? question.defaultValue);
  
  useEffect(() => {
    if (value !== undefined) setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  const percentage = ((localValue - question.min) / (question.max - question.min)) * 100;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end">
        <span className="text-[40px] font-black leading-none" style={{ color: theme.orange }}>
          {localValue}{question.unit}
        </span>
      </div>
      {question.description && <p className="text-sm" style={{ color: theme.textMuted }}>{question.description}</p>}
      <div className="relative pt-2">
        <input
          type="range" min={question.min} max={question.max} step={question.step} value={localValue}
          onChange={handleChange}
          className="w-full h-3 rounded-full appearance-none cursor-pointer slider-thumb"
          style={{ background: `linear-gradient(to right, ${theme.orange} 0%, ${theme.orange} ${percentage}%, ${theme.border} ${percentage}%, ${theme.border} 100%)` }}
        />
        <div className="flex justify-between mt-2 text-xs" style={{ color: theme.textLight }}>
          <span>{question.min}{question.unit}</span>
          <span>{question.max}{question.unit}</span>
        </div>
      </div>
    </div>
  );
};

// Number stepper
const NumberStepper = ({ question, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value ?? question.defaultValue);

  useEffect(() => {
    if (value !== undefined) setLocalValue(value);
  }, [value]);

  const handleChange = (newValue) => {
    const clampedValue = Math.max(question.min, Math.min(question.max, newValue));
    setLocalValue(clampedValue);
    onChange(clampedValue);
  };

  return (
    <div className="flex items-center justify-center gap-6">
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => handleChange(localValue - question.step)}
        disabled={localValue <= question.min}
        className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: theme.card, border: `2px solid ${theme.border}` }}
      >
        <Minus className="w-7 h-7" style={{ color: theme.text }} />
      </motion.button>
      <motion.div key={localValue} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="min-w-[100px] text-center">
        <span className="text-[64px] font-black leading-none" style={{ color: theme.orange }}>{localValue}</span>
        {question.unit && <span className="text-2xl font-bold ml-1" style={{ color: theme.textLight }}>{question.unit}</span>}
      </motion.div>
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => handleChange(localValue + question.step)}
        disabled={localValue >= question.max}
        className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: theme.card, border: `2px solid ${theme.border}` }}
      >
        <Plus className="w-7 h-7" style={{ color: theme.text }} />
      </motion.button>
    </div>
  );
};

// Rating component
const RatingInput = ({ question, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value ?? null);
  const handleSelect = (rating) => { setLocalValue(rating); onChange(rating); };
  const ratings = [];
  for (let i = question.min; i <= question.max; i += (question.step || 1)) ratings.push(i);

  return (
    <div className="space-y-3">
      {question.description && <p className="text-sm text-center" style={{ color: theme.textMuted }}>{question.description}</p>}
      <div className="flex justify-center gap-2 flex-wrap">
        {ratings.map((rating) => (
          <motion.button
            key={rating} whileTap={{ scale: 0.95 }} onClick={() => handleSelect(rating)}
            className="w-14 h-14 rounded-xl font-black text-xl transition-all duration-200"
            style={{
              backgroundColor: localValue === rating ? theme.orangeDim : theme.card,
              border: localValue === rating ? `2px solid ${theme.orange}` : `1px solid ${theme.border}`,
              color: localValue === rating ? theme.orange : theme.textLight
            }}
          >
            {rating}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// Toggle component
const ToggleInput = ({ question, value, onChange }) => (
  <div className="flex justify-center gap-4">
    {question.options.map((option) => (
      <motion.button
        key={String(option.value)} whileTap={{ scale: 0.95 }} onClick={() => onChange(option.value)}
        className="flex-1 max-w-[160px] py-5 rounded-2xl font-bold text-lg transition-all duration-200"
        style={{
          backgroundColor: value === option.value ? (option.value ? theme.green : theme.red) : theme.card,
          border: value === option.value ? 'none' : `1px solid ${theme.border}`,
          color: value === option.value ? '#fff' : theme.textMuted
        }}
      >
        {option.label}
      </motion.button>
    ))}
  </div>
);

// Instagram input with AI suggestions
const InstagramInput = ({ question, value, onChange, restoName }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  
  // Search Instagram when resto name changes
  const searchInstagram = async () => {
    if (!restoName || restoName.length < 3) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/search/instagram?query=${encodeURIComponent(restoName)}`);
      const data = await res.json();
      if (data.suggestions) {
        setSuggestions(data.suggestions);
        setShowSuggestions(true);
        setSearchDone(true);
      }
    } catch (err) {
      console.error('Instagram search failed:', err);
    }
    setLoading(false);
  };
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (restoName && restoName.length >= 3 && !searchDone) {
      const timer = setTimeout(searchInstagram, 500);
      return () => clearTimeout(timer);
    }
  }, [restoName]);
  
  const selectSuggestion = (handle) => {
    onChange(handle.replace('@', ''));
    setShowSuggestions(false);
  };
  
  return (
    <div className="space-y-3">
      {/* AI Search indicator */}
      {loading && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="flex items-center justify-center gap-2 py-3 rounded-xl"
          style={{ backgroundColor: theme.purpleDim || '#f3e8ff' }}
        >
          <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium text-purple-700">🔍 Recherche Instagram en cours...</span>
        </motion.div>
      )}
      
      {/* Suggestions */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && !value && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-2"
          >
            <p className="text-sm font-medium text-center" style={{ color: theme.textMuted }}>
              🎯 On a trouvé des comptes probables :
            </p>
            <div className="space-y-2">
              {suggestions.slice(0, 4).map((s, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => selectSuggestion(s.handle)}
                  className="w-full flex items-center justify-between gap-3 p-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ backgroundColor: theme.card, border: `2px solid ${theme.border}` }}
                >
                  <div className="flex items-center gap-3">
                    <Instagram className="w-5 h-5 text-[#E1306C]" />
                    <span className="font-semibold" style={{ color: theme.text }}>{s.handle}</span>
                  </div>
                  <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold">
                    Sélectionner
                  </span>
                </motion.button>
              ))}
            </div>
            <button
              onClick={() => setShowSuggestions(false)}
              className="w-full py-2 text-sm font-medium"
              style={{ color: theme.textMuted }}
            >
              ❌ Aucun de ceux-là / Saisir manuellement
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Manual input */}
      {(!showSuggestions || value) && (
        <div className="relative">
          <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#E1306C]" />
          <input
            type="text" 
            placeholder={question.placeholder} 
            value={value || ''} 
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-lg pl-12 pr-5 py-4 rounded-2xl outline-none transition-colors"
            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
          />
        </div>
      )}
      
      {question.description && <p className="text-sm text-center" style={{ color: theme.textMuted }}>{question.description}</p>}
      
      {/* Validation indicator */}
      {value && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-green-600">Compte Instagram sélectionné</span>
        </motion.div>
      )}
    </div>
  );
};

// Text input
const TextInput = ({ question, value, onChange }) => {
  const inputType = question.inputType || 'text';
  return (
    <div className="space-y-2">
      <input
        type={inputType}
        placeholder={question.placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-xl px-5 py-5 rounded-2xl outline-none transition-colors text-center font-semibold"
        style={{ backgroundColor: theme.card, border: `2px solid ${theme.border}`, color: theme.text }}
        autoComplete={inputType === 'email' ? 'email' : 'given-name'}
        autoCapitalize={inputType === 'email' ? 'none' : 'words'}
        spellCheck={inputType === 'email' ? 'false' : 'true'}
      />
      {question.required && !value && <p className="text-sm text-center" style={{ color: theme.orange }}>* Obligatoire pour recevoir votre diagnostic</p>}
    </div>
  );
};

// Phone input with international format and validation - WhatsApp style
const PhoneInput = ({ question, value, onChange }) => {
  const [countryCode, setCountryCode] = useState('+33');
  const [localNumber, setLocalNumber] = useState('');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  
  const countryCodes = [
    { code: '+33', flag: '🇫🇷', name: 'France', format: '6 12 34 56 78', length: 9 },
    { code: '+212', flag: '🇲🇦', name: 'Maroc', format: '6 12 34 56 78', length: 9 },
    { code: '+32', flag: '🇧🇪', name: 'Belgique', format: '4 12 34 56 78', length: 9 },
    { code: '+41', flag: '🇨🇭', name: 'Suisse', format: '79 123 45 67', length: 9 },
    { code: '+352', flag: '🇱🇺', name: 'Luxembourg', format: '621 123 456', length: 9 },
    { code: '+1', flag: '🇺🇸', name: 'USA/Canada', format: '555 123 4567', length: 10 },
  ];
  
  const currentCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];
  const isWhatsApp = question.isWhatsApp;
  
  const formatPhone = (input) => {
    const digits = input.replace(/\D/g, '');
    // Remove leading 0 if present
    const cleanDigits = digits.startsWith('0') ? digits.slice(1) : digits;
    // Format with spaces every 2 digits
    let formatted = '';
    for (let i = 0; i < cleanDigits.length && i < 10; i++) {
      if (i > 0 && i % 2 === 0) formatted += ' ';
      formatted += cleanDigits[i];
    }
    return formatted;
  };
  
  const handleChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setLocalNumber(formatted);
    // Store full international number
    const fullNumber = `${countryCode} ${formatted}`;
    onChange(fullNumber);
  };
  
  const handleCountryChange = (code) => {
    setCountryCode(code);
    setShowCountryPicker(false);
    if (localNumber) {
      onChange(`${code} ${localNumber}`);
    }
  };
  
  const digitsOnly = localNumber.replace(/\D/g, '');
  const isValidPhone = digitsOnly.length >= currentCountry.length;
  
  // WhatsApp color scheme
  const whatsappGreen = '#25D366';
  const borderColor = isValidPhone 
    ? (isWhatsApp ? whatsappGreen : theme.green) 
    : theme.border;
  
  return (
    <div className="space-y-3">
      {/* WhatsApp badge */}
      {isWhatsApp && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-center gap-2 py-2 px-4 rounded-full mx-auto w-fit"
          style={{ backgroundColor: '#E8F5E9' }}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#25D366">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="text-sm font-bold" style={{ color: '#128C7E' }}>
            WhatsApp uniquement
          </span>
        </motion.div>
      )}
      
      <div className="relative">
        {/* Country code selector */}
        <button
          type="button"
          onClick={() => setShowCountryPicker(!showCountryPicker)}
          className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ zIndex: 10 }}
        >
          <span className="text-xl">{currentCountry.flag}</span>
          <span className="text-sm font-bold" style={{ color: theme.textMuted }}>{countryCode}</span>
          <ChevronDown className="w-3 h-3" style={{ color: theme.textMuted }} />
        </button>
        
        {/* Country picker dropdown */}
        <AnimatePresence>
          {showCountryPicker && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute left-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border z-20 overflow-hidden"
              style={{ borderColor: theme.border }}
            >
              {countryCodes.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountryChange(country.code)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm font-medium" style={{ color: theme.text }}>{country.name}</span>
                  <span className="text-xs ml-auto" style={{ color: theme.textMuted }}>{country.code}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Phone input */}
        <input
          type="tel"
          inputMode="numeric"
          placeholder={currentCountry.format}
          value={localNumber}
          onChange={handleChange}
          className="w-full text-lg pl-28 pr-12 py-4 rounded-2xl outline-none transition-all"
          style={{ 
            backgroundColor: theme.card, 
            border: `2px solid ${borderColor}`, 
            color: theme.text 
          }}
          autoComplete="tel"
        />
        
        {/* Validation indicator */}
        {isValidPhone && (
          <motion.div 
            initial={{ scale: 0 }} 
            animate={{ scale: 1 }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            {isWhatsApp ? (
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            ) : (
              <Check className="w-6 h-6 text-green-500" />
            )}
          </motion.div>
        )}
      </div>
      
      {/* Description / helper text */}
      {question.description && (
        <p className="text-xs text-center px-4" style={{ color: isWhatsApp ? '#128C7E' : theme.textMuted }}>
          {question.description}
        </p>
      )}
      
      {/* Validation message */}
      {!isValidPhone && localNumber.length > 0 && (
        <p className="text-xs text-center" style={{ color: theme.orange }}>
          Numéro incomplet ({digitsOnly.length}/{currentCountry.length} chiffres)
        </p>
      )}
      {isValidPhone && (
        <motion.p 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="text-xs text-center flex items-center justify-center gap-1" 
          style={{ color: isWhatsApp ? whatsappGreen : theme.green }}
        >
          {isWhatsApp ? (
            <>
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp prêt
            </>
          ) : (
            <>
              <Check className="w-3 h-3" /> Numéro valide
            </>
          )}
        </motion.p>
      )}
    </div>
  );
};

// Currency input
const CurrencyInput = ({ question, value, onChange, allAnswers }) => {
  const [localValue, setLocalValue] = useState(value ?? question.defaultValue);
  const [inputValue, setInputValue] = useState(value ? value.toLocaleString('fr-FR') : question.defaultValue.toLocaleString('fr-FR'));

  useEffect(() => {
    if (value !== undefined) { setLocalValue(value); setInputValue(value.toLocaleString('fr-FR')); }
  }, [value]);

  const formatNumber = (num) => num.toLocaleString('fr-FR');

  // While typing: do NOT force the minimum (lets user clear & retype freely).
  // Only clamp the max so they can't go above the slider range.
  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    if (!raw) {
      setLocalValue(0);
      setInputValue('');
      onChange(0);
      return;
    }
    const num = parseInt(raw, 10) || 0;
    const capped = Math.min(question.max, num);
    setLocalValue(capped);
    setInputValue(formatNumber(capped));
    onChange(capped);
  };

  // On blur, enforce the minimum so we never submit < min.
  const handleInputBlur = () => {
    if (localValue < question.min) {
      setLocalValue(question.min);
      setInputValue(formatNumber(question.min));
      onChange(question.min);
    }
  };

  const handleSliderChange = (e) => {
    const num = parseInt(e.target.value);
    setLocalValue(num);
    setInputValue(formatNumber(num));
    onChange(num);
  };

  const percentage = ((Math.max(localValue, question.min) - question.min) / (question.max - question.min)) * 100;
  const quickAmounts = [20000, 50000, 75000, 100000, 150000];

  // Auto-computed percentage display (e.g., food cost % from matières premières / CA)
  const autoPct = (() => {
    if (!question.autoComputePercent || !allAnswers) return null;
    const base = allAnswers[question.autoComputePercent.of];
    if (!base || base <= 0 || !localValue) return null;
    const pct = Math.round((localValue / base) * 100);
    return pct;
  })();

  // Status label for the computed %
  const pctStatus = (() => {
    if (autoPct === null) return null;
    const isFoodCost = question.autoComputePercent?.storeAs === 'food_cost';
    const isMS = question.autoComputePercent?.storeAs === 'masse_salariale';
    if (isFoodCost) {
      if (autoPct <= 28) return { label: 'Excellent', color: theme.green };
      if (autoPct <= 32) return { label: 'Sain', color: theme.green };
      if (autoPct <= 38) return { label: 'À surveiller', color: theme.yellow };
      return { label: 'Élevé — à travailler', color: theme.red };
    }
    if (isMS) {
      if (autoPct <= 28) return { label: 'Excellent', color: theme.green };
      if (autoPct <= 34) return { label: 'Sain', color: theme.green };
      if (autoPct <= 40) return { label: 'À surveiller', color: theme.yellow };
      return { label: 'Élevé — à travailler', color: theme.red };
    }
    return null;
  })();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 transition-colors" style={{ backgroundColor: theme.card, border: `2px solid ${theme.border}` }}>
          <input
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={question.placeholder}
            className="bg-transparent text-4xl sm:text-5xl font-black text-right outline-none w-40 sm:w-52"
            style={{ color: theme.orange }}
          />
          <span className="text-3xl font-bold" style={{ color: theme.textLight }}>{question.unit}</span>
        </div>
        <p className="text-sm mt-2" style={{ color: theme.textMuted }}>/mois</p>
      </div>
      {question.description && <p className="text-sm text-center" style={{ color: theme.textMuted }}>{question.description}</p>}

      {/* Auto-computed % badge — reserved slot to avoid layout shift while typing */}
      {question.autoComputePercent && (
        <div className="min-h-[110px]">
          {autoPct !== null && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-4 mx-auto max-w-sm"
              style={{ backgroundColor: theme.card, border: `2px solid ${pctStatus?.color || theme.border}` }}
            >
              <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: theme.textMuted }}>
                Ça représente
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black" style={{ color: pctStatus?.color || theme.orange }}>
                  {autoPct}%
                </span>
                <span className="text-sm" style={{ color: theme.textMuted }}>de votre CA</span>
              </div>
              {pctStatus && (
                <p className="text-xs font-bold mt-1" style={{ color: pctStatus.color }}>
                  {pctStatus.label}
                </p>
              )}
            </motion.div>
          )}
        </div>
      )}

      <div className="relative pt-2">
        <input
          type="range" min={question.min} max={question.max} step={question.step} value={localValue}
          onChange={handleSliderChange}
          className="w-full h-3 rounded-full appearance-none cursor-pointer slider-thumb"
          style={{ background: `linear-gradient(to right, ${theme.orange} 0%, ${theme.orange} ${percentage}%, ${theme.border} ${percentage}%, ${theme.border} 100%)` }}
        />
        <div className="flex justify-between mt-2 text-xs" style={{ color: theme.textLight }}>
          <span>{formatNumber(question.min)}{question.unit}</span>
          <span>{formatNumber(question.max)}{question.unit}</span>
        </div>
      </div>

      {/* Quick amounts — only for CA mensuel (large amounts) */}
      {!question.autoComputePercent && (
        <div className="space-y-2">
          <p className="text-xs text-center" style={{ color: theme.textMuted }}>Sélection rapide</p>
          <div className="flex flex-wrap justify-center gap-2">
            {quickAmounts.map((amount) => (
              <motion.button
                key={amount} whileTap={{ scale: 0.95 }}
                onClick={() => { setLocalValue(amount); setInputValue(formatNumber(amount)); onChange(amount); }}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{
                  backgroundColor: localValue === amount ? theme.orange : theme.card,
                  border: localValue === amount ? 'none' : `1px solid ${theme.border}`,
                  color: localValue === amount ? '#fff' : theme.textMuted
                }}
              >
                {formatNumber(amount)}€
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Alert box
const FoodCostAlert = () => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-5 mt-4" style={{ backgroundColor: theme.yellowBg, border: `2px solid ${theme.yellowBorder}` }}>
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: theme.yellow }} />
      <div>
        <h4 className="font-bold mb-2" style={{ color: theme.yellow }}>Alerte importante</h4>
        <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
          <strong style={{ color: theme.text }}>La majorité des restaurants qui ferment ne connaissent pas leurs chiffres.</strong>
          <br /><br />Ne pas connaître son food cost empêche de piloter son restaurant correctement.
        </p>
      </div>
    </div>
  </motion.div>
);

const QuizPage = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [currentSubQ, setCurrentSubQ] = useState(0); // sub-question index within a subdivided screen
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev (for slide transitions)
  const [milestone, setMilestone] = useState(null); // { label, subline, icon } | null
  const milestoneTimerRef = useRef(null);
  const questionRefs = useRef([]);

  // Dynamic screen flow — switch to OUVERTURE branch when user picks "en_ouverture"
  const isOuverture = answers.project_status === 'en_ouverture';
  const SCREENS = isOuverture ? OUVERTURE_SCREENS : QUIZ_SCREENS;

  // Clamp currentScreen if user goes back and changes the flow
  const screen = SCREENS[Math.min(currentScreen, SCREENS.length - 1)];
  const totalScreens = SCREENS.length;
  const progress = ((Math.min(currentScreen, totalScreens - 1) + 1) / totalScreens) * 100;

  useEffect(() => {
    const typesWithDefaults = ['slider_euro', 'slider_percent', 'slider_number', 'number_stepper', 'currency_input'];
    screen.questions.forEach(q => {
      if (q.conditionalOn && answers[q.conditionalOn.question] !== q.conditionalOn.value) return;
      if (typesWithDefaults.includes(q.type) && answers[q.id] === undefined && q.defaultValue !== undefined) {
        setAnswers(prev => ({ ...prev, [q.id]: q.defaultValue }));
      }
    });
  }, [currentScreen, answers, screen.questions]);

  const shouldShowQuestion = (question) => {
    if (!question.conditionalOn) return true;
    return answers[question.conditionalOn.question] === question.conditionalOn.value;
  };

  const isQuestionComplete = (q) => {
    if (!shouldShowQuestion(q)) return true;
    if (q.type === 'text' || q.type === 'instagram') return answers[q.id]?.trim();
    if (q.type === 'multi') {
      const arr = answers[q.id];
      return Array.isArray(arr) && arr.length >= 1;
    }
    if (q.type === 'text_input') {
      const val = answers[q.id]?.trim() || '';
      if (q.inputType === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      }
      return val.length >= 2;
    }
    if (q.type === 'phone_input') {
      const phoneValue = answers[q.id] || '';
      const digitsOnly = phoneValue.replace(/[^\d]/g, '');
      return digitsOnly.length >= 11;
    }
    return answers[q.id] !== undefined;
  };

  // For subdivide mode: which questions are currently visible (just one in subdivide)
  const isSubdivide = screen.subdivideQuestions === true;
  const visibleQuestions = screen.questions.filter(shouldShowQuestion);
  const subTotal = visibleQuestions.length;
  const safeSubQ = Math.min(currentSubQ, Math.max(0, subTotal - 1));
  const activeSubQuestion = isSubdivide ? visibleQuestions[safeSubQ] : null;

  const isScreenComplete = () => {
    if (isSubdivide) {
      // In subdivide mode: only require the current sub-question to be complete
      return activeSubQuestion ? isQuestionComplete(activeSubQuestion) : true;
    }
    return screen.questions.every(isQuestionComplete);
  };

  // Question types that emit a value on every keystroke / drag.
  // For these we DON'T auto-scroll to the next question, otherwise the page
  // jumps while the user is still typing/dragging.
  const CONTINUOUS_TYPES = new Set([
    'currency_input', 'slider_euro', 'slider_percent', 'slider_number',
    'number_stepper', 'text', 'text_input', 'phone_input', 'number', 'instagram',
  ]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => {
      const next = { ...prev, [questionId]: value };
      // Find the current screen's question definition for auto-compute (search both screen sets)
      const allScreenSets = [QUIZ_SCREENS, OUVERTURE_SCREENS];
      outer: for (const set of allScreenSets) {
        for (const scr of set) {
          const q = scr.questions.find(qq => qq.id === questionId);
          if (q && q.autoComputePercent) {
            const base = next[q.autoComputePercent.of];
            if (base && base > 0 && value) {
              next[q.autoComputePercent.storeAs] = Math.round((value / base) * 100);
            }
            break outer;
          }
        }
      }
      return next;
    });
    // Subdivide: never auto-scroll (only one question is visible)
    if (isSubdivide) return;
    // Only auto-scroll for discrete choices (option / grid / rating / toggle).
    const currentQuestion = screen.questions.find(q => q.id === questionId);
    if (currentQuestion && CONTINUOUS_TYPES.has(currentQuestion.type)) return;
    const visQs = screen.questions.filter(q => shouldShowQuestion(q));
    const currentVisibleIndex = visQs.findIndex(q => q.id === questionId);
    if (currentVisibleIndex < visQs.length - 1) {
      setTimeout(() => {
        const nextQuestionId = visQs[currentVisibleIndex + 1].id;
        questionRefs.current[nextQuestionId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  };

  // Milestone copy per screen id — fires when user completes the screen
  const MILESTONE_COPY = {
    restaurant: { label: 'Profil créé', subline: 'On a ton ADN restaurant ✓', icon: '🎯' },
    finances: { label: 'Bloc finance validé', subline: 'Diagnostic financier prêt 📊', icon: '💰' },
    problemes: { label: 'Angles morts identifiés', subline: 'On voit où ça fuit 🎯', icon: '⚠️' },
    marketing: { label: 'Présence digitale OK', subline: 'On capte ton image 📱', icon: '📱' },
    digital: { label: 'Canaux de vente analysés', subline: 'On voit ton mix 🚀', icon: '🚀' },
    operations: { label: 'Équipe scannée', subline: 'On a la structure RH 👥', icon: '👥' },
    concept: { label: 'Vision capturée', subline: 'On comprend où vous allez 💡', icon: '💡' },
    ouverture_projet: { label: 'Projet cadré', subline: 'On a votre concept 🏗️', icon: '🏗️' },
    ouverture_chantiers: { label: 'Chantiers listés', subline: 'On sait quoi construire 🛠️', icon: '🛠️' },
    ouverture_experience: { label: 'Profil ouverture OK', subline: 'On connaît ton niveau 🎯', icon: '🎯' },
  };

  const triggerMilestone = (screenId) => {
    const m = MILESTONE_COPY[screenId];
    if (!m) return;
    setMilestone(m);
    if (milestoneTimerRef.current) clearTimeout(milestoneTimerRef.current);
    milestoneTimerRef.current = setTimeout(() => setMilestone(null), 1900);
  };

  // Cleanup on unmount
  useEffect(() => () => {
    if (milestoneTimerRef.current) clearTimeout(milestoneTimerRef.current);
  }, []);

  // Estimated minutes remaining (rough: 20s avg per screen)
  const estMinutes = Math.max(0, Math.round(((totalScreens - currentScreen - 1) * 20) / 60));

  // List of completed bloc labels — used in the diagnostic bar
  const blocsCompletes = SCREENS.slice(0, currentScreen)
    .map((s) => MILESTONE_COPY[s.id]?.label)
    .filter(Boolean);

  const handlePrev = () => {
    // Subdivide: step back through sub-questions first
    if (isSubdivide && currentSubQ > 0) {
      setDirection(-1);
      setCurrentSubQ((q) => q - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentScreen > 0) {
      setDirection(-1);
      setCurrentScreen(currentScreen - 1);
      // When entering a subdivide screen from the right, land on the LAST sub-question
      const prevScreen = SCREENS[currentScreen - 1];
      if (prevScreen?.subdivideQuestions) {
        const lastIdx = Math.max(0, prevScreen.questions.length - 1);
        setCurrentSubQ(lastIdx);
      } else {
        setCurrentSubQ(0);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = async () => {
    // Subdivide: advance through sub-questions before moving to next screen
    if (isSubdivide && currentSubQ < subTotal - 1) {
      setDirection(1);
      setCurrentSubQ((q) => q + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentScreen < totalScreens - 1) {
      // Fire milestone toast for the screen we're LEAVING
      triggerMilestone(screen.id);
      setDirection(1);
      setCurrentScreen(currentScreen + 1);
      setCurrentSubQ(0);  // reset sub-question position on screen change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsSubmitting(true);

      // Minimum display time for loading screen (4 seconds for engaging UX)
      const minDisplayTime = 4000;
      const startTime = Date.now();

      try {
        const response = await fetch(`${API_URL}/api/quiz/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers, restoName: answers.resto_name || '', instagram: answers.instagram || '', firstName: answers.first_name || '', phone: answers.phone || '' })
        });
        const result = await response.json();
        
        // Wait for minimum display time before navigating
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);
        
        setTimeout(() => {
          navigate(`/resultats/${result.id}`);
        }, remainingTime);
      } catch (error) {
        console.error('Error submitting quiz:', error);
        const localId = `local_${Date.now()}`;
        localStorage.setItem(`quiz_${localId}`, JSON.stringify(answers));
        
        // Wait for minimum display time before navigating
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);
        
        setTimeout(() => {
          navigate(`/resultats/${localId}`);
        }, remainingTime);
      }
    }
  };

  // Enhanced Loading Screen Component
  const LoadingScreen = () => {
    const [progress, setProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState(0);
    const [estimatedScore, setEstimatedScore] = useState(0);
    
    const loadingSteps = [
      { icon: '🔍', text: 'Analyse de vos données financières...', duration: 1200 },
      { icon: '📊', text: 'Calcul de votre Prime Cost...', duration: 1000 },
      { icon: '💰', text: 'Estimation des pertes évitables...', duration: 1200 },
      { icon: '🎯', text: 'Génération des recommandations...', duration: 1000 },
      { icon: '✨', text: 'Finalisation de votre diagnostic...', duration: 800 }
    ];
    
    useEffect(() => {
      // Animate progress bar
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 2;
        });
      }, 100);
      
      // Animate score counter (estimate based on answers)
      const baseScore = 45;
      const bonusFromAnswers = Math.min(35, Object.keys(answers).length * 2);
      const targetScore = baseScore + bonusFromAnswers;
      
      const scoreInterval = setInterval(() => {
        setEstimatedScore(prev => {
          if (prev >= targetScore) {
            clearInterval(scoreInterval);
            return targetScore;
          }
          return prev + 1;
        });
      }, 60);
      
      // Animate steps
      loadingSteps.forEach((step, index) => {
        const delay = loadingSteps.slice(0, index).reduce((acc, s) => acc + s.duration, 0);
        setTimeout(() => setCurrentStep(index + 1), delay);
      });
      
      return () => {
        clearInterval(progressInterval);
        clearInterval(scoreInterval);
      };
    }, []);
    
    // Calculate estimated monthly loss based on answers (only meaningful for "déjà ouvert")
    const ca = answers.ca_mensuel || 50000;
    const fc = answers.food_cost || 32;
    const ms = answers.masse_salariale || 35;
    const primeCost = fc + ms;
    const estimatedLoss = primeCost > 65 ? Math.round(ca * (primeCost - 65) / 100) : Math.round(ca * 0.05);
    const isOuvertureMode = answers.project_status === 'en_ouverture';
    
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: theme.bg }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="text-center max-w-md w-full"
        >
          {/* Animated Score Circle */}
          <div className="relative w-44 h-44 mx-auto mb-8">
            {/* Background circle */}
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="88"
                cy="88"
                r="80"
                fill="none"
                stroke={theme.border}
                strokeWidth="12"
              />
              {/* Animated progress circle */}
              <motion.circle
                cx="88"
                cy="88"
                r="80"
                fill="none"
                stroke={theme.orange}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={502}
                initial={{ strokeDashoffset: 502 }}
                animate={{ strokeDashoffset: 502 - (502 * progress) / 100 }}
                transition={{ duration: 0.1 }}
                style={{ filter: 'drop-shadow(0 0 8px rgba(240,90,26,0.4))' }}
              />
            </svg>
            {/* Score number in center */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span 
                className="text-5xl font-black"
                style={{ color: theme.orange }}
                key={estimatedScore}
              >
                {estimatedScore}
              </motion.span>
              <span className="text-sm font-medium" style={{ color: theme.textMuted }}>/100</span>
            </div>
            {/* Pulsing glow effect */}
            <motion.div
              className="absolute inset-0 rounded-full"
              animate={{ 
                boxShadow: [
                  '0 0 20px rgba(240,90,26,0.2)',
                  '0 0 40px rgba(240,90,26,0.4)',
                  '0 0 20px rgba(240,90,26,0.2)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          
          {/* Title */}
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold mb-2"
            style={{ color: theme.text }}
          >
            🧠 Analyse en cours...
          </motion.h2>
          
          {/* Teaser message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mb-6 p-4 rounded-xl"
            style={{ backgroundColor: theme.redBg || '#FEE2E2', border: `1px solid ${theme.red || '#EF4444'}` }}
          >
            <p className="text-sm font-medium" style={{ color: theme.red || '#DC2626' }}>
              {isOuvertureMode ? (
                <>🛡️ On évalue les <strong>risques majeurs</strong> de votre ouverture...</>
              ) : (
                <>💸 On détecte déjà <strong>~{estimatedLoss.toLocaleString('fr-FR')}€/mois</strong> de pertes évitables...</>
              )}
            </p>
          </motion.div>
          
          {/* Loading steps */}
          <div className="space-y-3 text-left">
            {loadingSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: currentStep > index ? 1 : 0.4,
                  x: 0
                }}
                transition={{ delay: index * 0.3, duration: 0.3 }}
                className="flex items-center gap-3 p-3 rounded-xl"
                style={{ 
                  backgroundColor: currentStep > index ? '#E8F5E9' : theme.card,
                  border: `1px solid ${currentStep > index ? '#4CAF50' : theme.border}`
                }}
              >
                <span className="text-xl">{currentStep > index ? '✅' : step.icon}</span>
                <span 
                  className="text-sm font-medium flex-1"
                  style={{ color: currentStep > index ? '#2E7D32' : theme.textMuted }}
                >
                  {step.text}
                </span>
                {currentStep === index + 1 && (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-t-transparent rounded-full"
                    style={{ borderColor: theme.orange, borderTopColor: 'transparent' }}
                  />
                )}
              </motion.div>
            ))}
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(to right, ${theme.orange}, #FF5722)` }}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs mt-2 font-medium" style={{ color: theme.textMuted }}>
              {progress}% complété
            </p>
          </div>
        </motion.div>
      </div>
    );
  };

  if (isSubmitting) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme.bg }} data-testid="quiz-page">
      {/* Floating milestone toast (Duolingo-style dopamine on screen complete) */}
      <QuizMilestoneToast
        show={!!milestone}
        label={milestone?.label || ''}
        subline={milestone?.subline}
        icon={milestone?.icon}
      />

      {/* Progress Bar Premium */}
      <div className="w-full px-4 pt-4">
        <div className="relative">
          <div className="h-2 rounded-full" style={{ backgroundColor: theme.border }}>
            <motion.div 
              className="h-full rounded-full" 
              style={{ background: `linear-gradient(to right, ${theme.orange}, #FF7A3D, #FF5722)` }} 
              initial={{ width: 0 }} 
              animate={{ width: `${progress}%` }} 
              transition={{ duration: 0.4, ease: "easeOut" }} 
            />
          </div>
          {/* Progress indicator */}
          <motion.div 
            className="absolute -top-1 w-4 h-4 rounded-full shadow-lg"
            style={{ 
              left: `calc(${progress}% - 8px)`,
              background: `linear-gradient(135deg, ${theme.orange}, #FF5722)`,
              border: '2px solid white'
            }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>

        {/* Live diagnostic bar — keeps user hooked: "diagnostic en cours..." + bloc badges + time left */}
        <QuizDiagnosticBar
          currentScreen={currentScreen}
          totalScreens={totalScreens}
          estMinutes={estMinutes}
          blocsCompletes={blocsCompletes}
        />

        {/* Intrigue widget — locked → unlock → live loss counter (first 3 screens only) */}
        <QuizIntrigueWidget
          answers={answers}
          currentScreen={currentScreen}
          screenId={screen.id}
        />

        {/* Contextual message */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-[10px] font-medium" style={{ color: theme.textMuted }}>
            {({
              restaurant: '🎯 Commençons par ton restaurant',
              finances: '💰 Parlons de vos chiffres',
              problemes: '⚠️ Identifions tes problèmes',
              marketing: '📱 Ta présence digitale',
              digital: '🚀 Tes canaux de vente',
              operations: '👥 Ton équipe',
              concept: '💡 Ta vision',
              contact: '📝 Dernière étape !',
              ouverture_projet: '🏗️ Cadre de votre ouverture',
              ouverture_chantiers: '🛠️ Ce qu\'on construit ensemble',
              ouverture_experience: '🎯 Ton expérience & tes peurs',
            }[screen.id]) || '📝 Continuons'}
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.orangeDim, color: theme.orange }}>
            {Math.round(progress)}% complété
          </span>
        </div>
      </div>

      {/* === Animated screen container — slide horizontally between screens === */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={`screen-${currentScreen}`}
          custom={direction}
          initial={(d) => ({ opacity: 0, x: d > 0 ? 40 : -40 })}
          animate={{ opacity: 1, x: 0 }}
          exit={(d) => ({ opacity: 0, x: d > 0 ? -40 : 40 })}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 flex flex-col"
        >
          {/* Screen Header */}
          <div className="px-6 pt-4 pb-4">
            <div className="flex items-center gap-3 mb-2">
              <motion.span
                key={`icon-${currentScreen}`}
                initial={{ scale: 0.6, rotate: -12, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 280, damping: 18 }}
                className="text-3xl"
              >
                {screen.icon}
              </motion.span>
              <span className="text-xs font-bold tracking-[2px] uppercase" style={{ color: theme.orange }}>{currentScreen + 1}/{totalScreens}</span>
            </div>
            <h1 className="font-bebas text-3xl sm:text-4xl tracking-wide" style={{ color: theme.text }}>{screen.title}</h1>
          </div>

          {/* Sabril Coach — contextual tip for this screen (hidden in subdivide mode to avoid duplication with QuizQuestionContext) */}
          {screen.sabrilTip && !isSubdivide && (
            <div className="px-6 pb-2">
              <QuizSabrilCoach
                message={screen.sabrilTip}
                tone={screen.sabrilTone || 'default'}
                testid={`quiz-coach-screen-${screen.id}`}
              />
            </div>
          )}

          {/* Questions */}
          <div className="flex-1 px-6 pb-32 space-y-10">
            {/* Reassurance Block for Contact Step */}
            {screen.showReassurance && screen.reassuranceBlock && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5"
                style={{ backgroundColor: '#E8F5E9', border: `2px solid #4CAF50` }}
              >
                <h3 className="font-bold text-lg mb-3" style={{ color: '#2E7D32' }}>
                  {screen.reassuranceBlock.title}
                </h3>
                <div className="space-y-2">
              {screen.reassuranceBlock.points.map((point, idx) => (
                <p key={idx} className="text-sm leading-relaxed" style={{ color: '#1B5E20' }}>
                  {point}
                </p>
              ))}
            </div>
          </motion.div>
        )}
        
        <AnimatePresence mode="wait">
          {screen.questions.map((question, qIndex) => {
            if (!shouldShowQuestion(question)) return null;
            // Subdivide: only render the active sub-question
            if (isSubdivide && question.id !== activeSubQuestion?.id) return null;
            return (
              <motion.div key={`${question.id}-${isSubdivide ? safeSubQ : 'all'}`} ref={el => questionRefs.current[question.id] = el} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: isSubdivide ? 0 : qIndex * 0.1, duration: 0.4 }} className="space-y-5">
                {/* Subdivide: progress dots + WHY context block */}
                {isSubdivide && (
                  <>
                    {/* Sub-question progress dots */}
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      {visibleQuestions.map((_, i) => (
                        <motion.span
                          key={i}
                          className="rounded-full"
                          animate={{
                            width: i === safeSubQ ? 22 : 6,
                            height: 6,
                            backgroundColor: i < safeSubQ ? theme.orange : i === safeSubQ ? theme.orange : theme.border,
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      ))}
                      <span className="text-[10px] font-black ml-2" style={{ color: theme.textMuted }}>
                        {safeSubQ + 1}/{subTotal}
                      </span>
                    </div>
                    {/* Sabril WHY + BENEFIT context — WhatsApp-style floating bubble */}
                    {(question.why || question.benefit) && (
                      <QuizQuestionContext
                        questionId={question.id}
                        why={question.why}
                        benefit={question.benefit}
                        confidential={!!question.confidential}
                      />
                    )}
                  </>
                )}

                {/* === APP-CARD WRAPPER (subdivide mode) — wraps label + tip + input === */}
                {isSubdivide ? (
                  <motion.div
                    initial={{ opacity: 0, y: 16, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="rounded-3xl overflow-hidden relative"
                    data-testid="quiz-app-card"
                    style={{
                      background: '#FFFFFF',
                      boxShadow: '0 20px 50px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04), 0 0 0 1px rgba(240,90,26,0.08) inset',
                    }}
                  >
                    {/* Orange accent strip top */}
                    <div className="h-1" style={{ background: 'linear-gradient(90deg, #F05A1A 0%, #FF7A3D 50%, #F05A1A 100%)' }} />
                    {/* Subtle radial glow top-right */}
                    <div
                      className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.10) 0%, transparent 70%)' }}
                    />
                    <div className="relative p-5 space-y-4">
                      <h2 className="font-bold text-lg" style={{ color: theme.text }}>{question.label}</h2>

                      {/* Sabril per-question contextual tip */}
                      {(() => {
                        const dyn = getDynamicSabrilTip(question.id, answers);
                        const tipMessage = dyn?.message || question.sabrilTip;
                        const tipTone = dyn?.tone || question.sabrilTone || 'default';
                        if (!tipMessage) return null;
                        return (
                          <QuizSabrilCoach
                            key={`${question.id}-${tipMessage}`}
                            message={tipMessage}
                            tone={tipTone}
                            compact
                            testid={`quiz-coach-q-${question.id}`}
                          />
                        );
                      })()}

                      {/* Inputs */}
                      {question.type === 'text' && (
                        <input type="text" placeholder={question.placeholder} value={answers[question.id] || ''} onChange={(e) => handleAnswer(question.id, e.target.value)}
                          className="w-full text-lg px-5 py-4 rounded-2xl outline-none transition-colors"
                          style={{ backgroundColor: theme.bg, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      )}
                      {question.type === 'instagram' && <InstagramInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} restoName={answers.resto_name} />}
                      {question.type === 'choice' && (
                        <div className="space-y-3">
                          {question.options.map((option) => (
                            <OptionButton key={option.value} option={option} selected={answers[question.id] === option.value} onClick={() => handleAnswer(question.id, option.value)} />
                          ))}
                          {question.id === 'connait_foodcost' && (answers[question.id] === 'non' || answers[question.id] === 'sais_pas') && <FoodCostAlert />}
                        </div>
                      )}
                      {question.type === 'grid' && question.id === 'resto_type' && (
                        <CuisineTypeGrid 
                          question={question} 
                          value={answers[question.id]} 
                          onChange={(value) => handleAnswer(question.id, value)}
                          restoName={answers.resto_name}
                          instagram={answers.instagram}
                        />
                      )}
                      {question.type === 'grid' && question.id !== 'resto_type' && (
                        <div className="grid grid-cols-2 gap-3">
                          {question.options.map((option) => (
                            <GridButton key={option.value} option={option} selected={answers[question.id] === option.value} onClick={() => handleAnswer(question.id, option.value)} />
                          ))}
                        </div>
                      )}
                      {(question.type === 'slider_euro' || question.type === 'slider_percent' || question.type === 'slider_number') && <SliderInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'number_stepper' && <NumberStepper question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'rating' && <RatingInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'toggle' && <ToggleInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'text_input' && <TextInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'phone_input' && <PhoneInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                      {question.type === 'currency_input' && <CurrencyInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} allAnswers={answers} />}
                      {question.type === 'multi' && <MultiSelect question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                    </div>
                  </motion.div>
                ) : (
                  <>
                <h2 className="font-bold text-lg" style={{ color: theme.text }}>{question.label}</h2>

                {/* Sabril per-question contextual tip — dynamic version overrides static */}
                {(() => {
                  const dyn = getDynamicSabrilTip(question.id, answers);
                  const tipMessage = dyn?.message || question.sabrilTip;
                  const tipTone = dyn?.tone || question.sabrilTone || 'default';
                  if (!tipMessage) return null;
                  return (
                    <QuizSabrilCoach
                      key={`${question.id}-${tipMessage}`}
                      message={tipMessage}
                      tone={tipTone}
                      compact
                      testid={`quiz-coach-q-${question.id}`}
                    />
                  );
                })()}

                {question.type === 'text' && (
                  <input type="text" placeholder={question.placeholder} value={answers[question.id] || ''} onChange={(e) => handleAnswer(question.id, e.target.value)}
                    className="w-full text-lg px-5 py-4 rounded-2xl outline-none transition-colors"
                    style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                  />
                )}
                {question.type === 'instagram' && <InstagramInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} restoName={answers.resto_name} />}
                {question.type === 'choice' && (
                  <div className="space-y-3">
                    {question.options.map((option) => (
                      <OptionButton key={option.value} option={option} selected={answers[question.id] === option.value} onClick={() => handleAnswer(question.id, option.value)} />
                    ))}
                    {question.id === 'connait_foodcost' && (answers[question.id] === 'non' || answers[question.id] === 'sais_pas') && <FoodCostAlert />}
                  </div>
                )}
                {question.type === 'grid' && question.id === 'resto_type' && (
                  <CuisineTypeGrid 
                    question={question} 
                    value={answers[question.id]} 
                    onChange={(value) => handleAnswer(question.id, value)}
                    restoName={answers.resto_name}
                    instagram={answers.instagram}
                  />
                )}
                {question.type === 'grid' && question.id !== 'resto_type' && (
                  <div className="grid grid-cols-2 gap-3">
                    {question.options.map((option) => (
                      <GridButton key={option.value} option={option} selected={answers[question.id] === option.value} onClick={() => handleAnswer(question.id, option.value)} />
                    ))}
                  </div>
                )}
                {(question.type === 'slider_euro' || question.type === 'slider_percent' || question.type === 'slider_number') && <SliderInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'number_stepper' && <NumberStepper question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'rating' && <RatingInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'toggle' && <ToggleInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'text_input' && <TextInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'phone_input' && <PhoneInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                {question.type === 'currency_input' && <CurrencyInput question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} allAnswers={answers} />}
                {question.type === 'multi' && <MultiSelect question={question} value={answers[question.id]} onChange={(value) => handleAnswer(question.id, value)} />}
                  </>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
        </motion.div>
      </AnimatePresence>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6" style={{ background: `linear-gradient(to top, ${theme.bg} 0%, ${theme.bg} 80%, transparent 100%)` }}>
        <div className="flex gap-3">
          {currentScreen > 0 && (
            <motion.button
              onClick={handlePrev}
              whileTap={{ scale: 0.98 }}
              data-testid="prev-btn"
              className="flex-shrink-0 px-5 py-5 rounded-2xl font-bold text-sm uppercase tracking-wide transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: theme.card,
                color: theme.text,
                border: `2px solid ${theme.border}`,
              }}
              aria-label="Étape précédente"
            >
              <ArrowRight className="w-5 h-5" style={{ transform: 'rotate(180deg)' }} />
              <span className="hidden sm:inline">Précédent</span>
            </motion.button>
          )}
          <motion.button
            onClick={handleNext} disabled={!isScreenComplete()}
            animate={isScreenComplete() ? { scale: [1, 1.02, 1] } : {}}
            transition={{ duration: 0.3 }}
            data-testid="next-btn"
            className="flex-1 py-5 rounded-2xl font-black text-base uppercase tracking-wide transition-all flex items-center justify-center gap-3"
            style={{
              backgroundColor: isScreenComplete() ? theme.orange : theme.border,
              color: isScreenComplete() ? '#fff' : theme.textLight,
              boxShadow: isScreenComplete() ? '0 8px 32px rgba(240,90,26,0.35)' : 'none',
              cursor: isScreenComplete() ? 'pointer' : 'not-allowed'
            }}
          >
            {currentScreen === totalScreens - 1 ? 'ACCÉDER À MON DIAGNOSTIC' : 'SUIVANT'}
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>
        {/* Soft trust hint — no "PRSNL" pitch during the free audit */}
        <p className="block text-center text-[11px] mt-4 italic" style={{ color: theme.textMuted }}>
          🔒 Audit gratuit · {totalScreens - currentScreen - 1 > 0 ? `${totalScreens - currentScreen - 1} étape${totalScreens - currentScreen - 1 > 1 ? 's' : ''} avant votre diagnostic complet` : 'Diagnostic prêt à la fin'}
        </p>
      </div>

      {/* Custom slider styles */}
      <style>{`
        input[type="range"].slider-thumb { -webkit-appearance: none; appearance: none; }
        input[type="range"].slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none; width: 28px; height: 28px; border-radius: 50%;
          background: white; cursor: pointer; border: 4px solid ${theme.orange}; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        input[type="range"].slider-thumb::-moz-range-thumb {
          width: 28px; height: 28px; border-radius: 50%; background: white; cursor: pointer;
          border: 4px solid ${theme.orange}; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `}</style>
    </div>
  );
};

export default QuizPage;
